import React, { useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { IconScout } from '@/components/ui/images/IconScout';
import { Spinner } from '@/components/ui/loading';
import { InfoBubble } from '@/components/ui/tooltips/InfoBubble';
import { colors } from '@/assets/styles/colors';
import { zIndex } from '@/assets/styles';
import { Text } from '@/components/ui/text/Text';
import { Card } from '@/components/ui/cards/Card';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { useSelector, useSelectorPosition, useSelectorKeyboard, useSelectorPagination } from '@/hooks/ui/selector';
import { SelectorOptionItem } from './SelectorOptionItem';
import { getSelectorColors } from './Selector.types';
import type { SelectorProps, SelectorOption, SelectorColor } from './Selector.types';


// Re-export types for convenience
export type { SelectorProps, SelectorOption, SelectorColor };

/**
 * Advanced selector component with search, single/multiple selection, and keyboard navigation
 * Dropdown width matches trigger width, with truncated text and tooltips for overflow.
 * 
 * @example
 * ```tsx
 * <Selector
 *   label="Select option"
 *   options={[{ value: '1', label: 'Option 1' }]}
 *   value={selected}
 *   onChange={setSelected}
 * />
 * ```
 */
export const Selector: React.FC<SelectorProps> = ({
  id,
  label,
  value,
  onChange,
  options,
  supportingText,
  placeholder = 'Selecciona una opción',
  color = 'default',
  disabled = false,
  required = false,
  className = '',
  selectClassName = '',
  labelClassName = '',
  'data-testid': dataTestId,
  supportingClassName = '',
  multiple = false,
  searchable = true,
  menuClassName = '',
  optionClassName = '',
  portal = false,
  hideChevron = false,
  hideLabel = false,
  chevronColor,
  chevronWrapperClassName = 'items-end pb-1',
  tooltipText,
  // Pagination props
  onLoadMore,
  hasNextPage = false,
  isLoadingMore = false,
  isLoading = false,
  onSearchChange,
}) => {
  // Constants for removed props with defaults
  const maxMenuHeight = 260;
  const searchPlaceholder = 'Buscar...';
  // Core selector state and logic
  const {
    baseId,
    isMultiple,
    open,
    search,
    activeIndex,
    isFocused,
    triggerWidth,
    triggerRef,
    listRef,
    currentValue,
    selectedValues,
    filtered,
    focusSelectedIndex,
    setSearch,
    setActiveIndex,
    toggleValue,
    openMenu: baseOpenMenu,
    closeMenu,
  } = useSelector({
    id,
    value,
    options,
    multiple,
    disabled,
    onChange,
    onSearchChange,
  });

  // Pagination with infinite scroll
  const { sentinelRef } = useSelectorPagination({
    listRef,
    hasNextPage,
    isLoadingMore,
    onLoadMore,
    open,
  });

  // Position calculation
  const {
    openDirection,
    portalStyle,
    computeAndSetPosition,
  } = useSelectorPosition({
    triggerRef,
    maxMenuHeight,
    searchable,
    portal,
    open,
    options: filtered,
    search,
  });

  // Extended openMenu that also computes position
  const openMenu = () => {
    baseOpenMenu();
    computeAndSetPosition();
  };

  // Keyboard navigation
  const { handleTriggerKey, handleListKey } = useSelectorKeyboard({
    open,
    disabled,
    filtered,
    activeIndex,
    focusSelectedIndex,
    listRef,
    setActiveIndex,
    toggleValue,
    openMenu,
    closeMenu,
    search,
  });

  // Colors based on state
  const {
    label: labelColor,
    supporting: supportingColor,
    body: bodyColor,
    border: borderColor,
    icon: iconColor,
  } = getSelectorColors({
    variant: color,
    disabled,
    focused: isFocused,
    open,
  });

  // Focus management after opening
  useLayoutEffect(() => {
    if (!open) return;
    if (searchable) {
      const searchInput = document.getElementById(`${baseId}-search`) as HTMLInputElement | null;
      searchInput?.focus();
    } else if (listRef.current) {
      const initialIndex = focusSelectedIndex >= 0 ? focusSelectedIndex : 0;
      setActiveIndex(initialIndex);
      listRef.current.querySelector<HTMLElement>(`[data-idx="${initialIndex}"]`)?.focus();
    }
  }, [open, searchable, baseId, focusSelectedIndex, listRef, setActiveIndex]);

  // Click outside handler
  useEffect(() => {
    if (!open) return;
    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (triggerRef.current?.contains(event.target as Node)) return;
      if (listRef.current?.parentElement?.contains(event.target as Node)) return;
      closeMenu();
    };
    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [open, closeMenu, triggerRef, listRef]);

  // Border radius based on open direction
  const borderRadiusStyle = openDirection === 'down'
    ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 }
    : { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 };

  // Transform for upward opening (portal mode)
  const portalTransformStyle = openDirection === 'up' ? { transform: 'translateY(-100%)' } : {};

  // Dropdown content
  const listContent = (
    <Card
      elevation={2}
      className={`p-0 ${menuClassName}`}
      style={{
        zIndex: portal ? 6000 : zIndex.dropdown,
        ...borderRadiusStyle,
        ...(portal && portalStyle
          ? {
            position: 'absolute',
            top: portalStyle.top,
            left: portalStyle.left,
            width: portalStyle.width,
            ...portalTransformStyle,
          }
          : { width: triggerWidth ?? undefined }),
      }}
    >
      {searchable && (
        <div className="px-1 py-2">
          <LabelInput
            id={`${baseId}-search`}
            placeholder={searchPlaceholder}
            value={search}
            onChange={(val) => setSearch(val)}
            iconRight={<IconScout name="search" />}
            className="mb-0"
            inputClassName="!border-0 !border-b !rounded-none"
          />
        </div>
      )}
      <ul
        id={`${baseId}-listbox`}
        ref={listRef}
        role="listbox"
        aria-labelledby={baseId}
        aria-busy={isLoadingMore || isLoading}
        {...(isMultiple ? { 'aria-multiselectable': true } : {})}
        tabIndex={-1}
        onKeyDown={handleListKey}
        className={`overflow-auto outline-none focus:outline-none ${optionClassName}`}
        style={{ maxHeight: maxMenuHeight }}
      >
        {isLoading && (
          <li className="px-4 py-4 flex justify-center items-center" aria-disabled="true">
            <Spinner size="sm" />
          </li>
        )}
        {!isLoading && filtered.length === 0 && !isLoadingMore && (
          <li className="px-4 py-2 select-none" aria-disabled="true">
            <Text variant="body-regular" color={colors.primary.main}>
              Sin resultados
            </Text>
          </li>
        )}
        {!isLoading && filtered.map((option, index) => (
          <SelectorOptionItem
            key={`${option.value}-${index}`}
            option={option}
            index={index}
            baseId={baseId}
            selected={selectedValues.includes(option.value)}
            active={index === activeIndex}
            isMultiple={isMultiple}
            onToggle={toggleValue}
            onMouseEnter={() => setActiveIndex(index)}
          />
        ))}
        {/* Loading indicator for infinite scroll */}
        {isLoadingMore && (
          <li className="px-4 py-3 flex justify-center items-center" aria-disabled="true">
            <Spinner size="sm" />
          </li>
        )}
        {/* Sentinel element for IntersectionObserver */}
        {hasNextPage && !isLoadingMore && (
          <li aria-hidden="true">
            <div ref={sentinelRef} className="h-1" />
          </li>
        )}
      </ul>
    </Card>
  );

  const dropdownNode = portal
    ? (open && createPortal(listContent, document.body))
    : (open && (
      <div
        className="absolute left-0"
        style={{
          zIndex: zIndex.dropdown,
          width: triggerWidth ?? undefined,
          top: openDirection === 'down' ? '100%' : undefined,
          bottom: openDirection === 'up' ? '100%' : undefined,
        }}
      >
        {listContent}
      </div>
    ));

  // Determine displayed text
  const displayedText = (() => {
    if (isMultiple) {
      if (selectedValues.length === 0) {
        return <span style={{ color: colors.grays.neutral99 }}>{placeholder}</span>;
      }
      const suffix = selectedValues.length > 1 ? 's' : '';
      return `${selectedValues.length} seleccionada${suffix}`;
    }
    if (currentValue !== undefined) {
      const match = options.find(option => option.value === currentValue);
      if (match) return match.label;
      return <span style={{ color: colors.grays.neutral99 }}>{placeholder}</span>;
    }
    return <span style={{ color: colors.grays.neutral99 }}>{placeholder}</span>;
  })();

  // Chevron hidden when disabled or explicitly hidden
  const showChevron = !hideChevron && !disabled;

  return (
    <div className={`relative ${className}`}>
      {!hideLabel && (
        <div className="flex items-center justify-between">
          <Text
            variant="small-regular"
            color={labelColor}
            className={labelClassName}
          >
            {label}
            {required && <span className="ml-1">*</span>}
            {tooltipText && <InfoBubble text={tooltipText} />}
          </Text>
        </div>
      )}
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          id={baseId}
          data-testid={dataTestId}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? `${baseId}-listbox` : undefined}
          aria-multiselectable={isMultiple || undefined}
          disabled={disabled}
          onClick={() => (open ? closeMenu() : openMenu())}
          onKeyDown={handleTriggerKey}
          className={`w-full text-sm text-left pr-8 pb-[7px] pt-[6px] bg-transparent outline-none border-b overflow-hidden text-ellipsis whitespace-nowrap ${
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
          } transition-colors ${selectClassName}`}
          style={{
            borderBottomColor: borderColor,
            borderBottomWidth: 1,
            borderBottomStyle: 'solid',
            color: bodyColor,
          }}
        >
          {displayedText}
        </button>
        {showChevron && (
          <div
            className={`absolute right-1 top-0 h-full flex pointer-events-none ${chevronWrapperClassName}`}
            aria-hidden="true"
          >
            <IconScout name="angleDown" size="lg" color={chevronColor ?? iconColor} />
          </div>
        )}
        {dropdownNode}
      </div>
      {supportingText && (
        <Text variant="small-regular" className={`mt-1 ${supportingClassName}`} color={supportingColor}>
          {supportingText}
        </Text>
      )}
    </div>
  );
};

export default Selector;
